import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { CONFIG } from "../utility/constants/common-constants";
import { getPageNameFromPath } from "../utility/functions/common-functions";

const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: CONFIG.instrumentationKey,
    enableAutoRouteTracking: false,
  },
});

appInsights.loadAppInsights();

// Track the last page view time and path to calculate duration
let lastPageViewTime = performance.now();
let lastPagePath = window.location.pathname;

interface PageViewOptions {
  path: string;
  username?: string;
  uri?: string;
}

export const trackAppInsightsPageView = ({
  path,
  username = "anonymous",
  uri = window.location.href,
}: PageViewOptions) => {
  const now = performance.now();
  const duration = Math.round(now - lastPageViewTime);

  // Track the previous page view with duration
  appInsights.trackPageView({
    name: username,
    uri: window.location.origin + lastPagePath, // Previous page's URI
    measurements: {
      duration,
    },
    properties: {
      username,
      pagename: getPageNameFromPath(lastPagePath), // Previous page
      nextPageName: getPageNameFromPath(path), // New/current page
    },
  });

  // Update state to the current page for next calculation
  lastPageViewTime = now;
  lastPagePath = path;
};

// Used when session times out or user logs out without navigating
export const trackCurrentPageDuration = (username: string = "anonymous") => {
  const now = performance.now();
  const duration = Math.round(now - lastPageViewTime);

  appInsights.trackPageView({
    name: username,
    uri: window.location.origin + lastPagePath,
    measurements: {
      duration,
    },
    properties: {
      username,
      pagename: getPageNameFromPath(lastPagePath),
      endedBy: "manual", // helps indicate logout/session timeout event
    },
  });

  lastPageViewTime = now;
};

// Set user context
export const setAuthenticatedUser = (userId: string) => {
  if (userId) {
    appInsights.setAuthenticatedUserContext(userId);
    trackEvent("PatientSessionStart", {
      patientProfileId: userId,
      timestamp: new Date().toISOString(),
    });
  }
};

// Clear user context & track session end
export const clearAuthenticatedUser = (userId: string) => {
  trackEvent("PatientSessionEnd", {
    patientProfileId: userId,
    timestamp: new Date().toISOString(),
  });
  appInsights.clearAuthenticatedUserContext();
};

// Generic event tracker
export const trackEvent = (
  name: string,
  properties?: { [key: string]: any }
): void => {
  appInsights.trackEvent({
    name,
    properties: {
      ...properties,
      timestamp: new Date().toISOString(),
    },
  });
};

export default appInsights;
